import {
	DateRangePicker,
	ExportRequestModal,
	ExportSuccessModal,
	FormFieldControl,
	MultiSelect,
} from "@components";
import { SearchLg } from "@untitledui/icons";
import { useEffect, useMemo, useState } from "react";
import type { DateValue, Selection } from "react-aria-components";
import { useChecklistTemplatesLookup } from "@/features/forms/hooks/useChecklistTemplateQueries";
import type { SelectItemType } from "@/shared/components/select/select-shared";
import { useExportChecklists } from "@/shared/hooks";
import { REPORT_TYPE } from "@/shared/types/generate-report-request";
import {
	buildGenerateReportRequest,
	formatDateValueToApi,
	normalizeExportFilterIds,
	selectionToNumberIds,
	validateExportDateRange,
} from "@/shared/utils";

type DateRangeValue = {
	start: DateValue;
	end: DateValue;
} | null;

type InspectionsExportModalProps = {
	isOpen: boolean;
	onClose: () => void;
};

export function InspectionsExportModal({
	isOpen,
	onClose,
}: InspectionsExportModalProps) {
	const [selectedTemplateIds, setSelectedTemplateIds] = useState<number[]>([]);
	const [dateRange, setDateRange] = useState<DateRangeValue>(null);
	const [pullAllSinceStart, setPullAllSinceStart] = useState(false);
	const [templatesError, setTemplatesError] = useState<string | null>(null);
	const [dateError, setDateError] = useState<string | null>(null);
	const [isSuccessOpen, setIsSuccessOpen] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const { data: templatesLookup = [] } = useChecklistTemplatesLookup({
		enabled: isOpen,
	});

	const templateItems = useMemo<SelectItemType[]>(() => {
		const seenTemplateIds = new Set<number>();

		return templatesLookup.reduce<SelectItemType[]>((items, template) => {
			if (seenTemplateIds.has(template.templateId)) {
				return items;
			}

			seenTemplateIds.add(template.templateId);
			items.push({
				id: String(template.templateId),
				label: template.checklistName ?? `Template #${template.templateId}`,
			});
			return items;
		}, []);
	}, [templatesLookup]);

	const allTemplateIds = useMemo(
		() => templateItems.map((item) => Number(item.id)),
		[templateItems]
	);

	const selectedKeys = useMemo(
		() => new Set<string>(selectedTemplateIds.map(String)),
		[selectedTemplateIds]
	);

	const exportMutation = useExportChecklists({
		onSuccess: (response) => {
			onClose();
			setSuccessMessage(response.message);
			setIsSuccessOpen(true);
		},
	});

	useEffect(() => {
		if (!isOpen) {
			setSelectedTemplateIds([]);
			setDateRange(null);
			setPullAllSinceStart(false);
			setTemplatesError(null);
			setDateError(null);
		}
	}, [isOpen]);

	const handleTemplatesSelectionChange = (keys: Selection) => {
		setSelectedTemplateIds(selectionToNumberIds(keys, allTemplateIds));
		setTemplatesError(null);
	};

	const handleSubmit = () => {
		let hasError = false;

		const validationError = validateExportDateRange(
			dateRange?.start,
			dateRange?.end,
			{ pullAllSinceStart, isRequired: false }
		);
		if (validationError) {
			setDateError(validationError);
			hasError = true;
		} else {
			setDateError(null);
		}

		if (hasError) {
			return;
		}

		exportMutation.mutate(
			buildGenerateReportRequest({
				type: REPORT_TYPE.Checklist,
				initialDate: formatDateValueToApi(dateRange?.start),
				finalDate: formatDateValueToApi(dateRange?.end),
				pullAllSinceStart,
				templateIds: normalizeExportFilterIds(
					selectedTemplateIds,
					allTemplateIds
				),
			})
		);
	};

	return (
		<>
			<ExportRequestModal
				description="Digite o nome do template e selecione a data"
				isOpen={isOpen}
				isSubmitting={exportMutation.isPending}
				onClose={onClose}
				onSubmit={handleSubmit}
				title="Exportar Vistorias"
			>
				<FormFieldControl
					className="xs:grid xs:grid-cols-[85px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={templatesError ?? undefined}
					htmlFor="inspections-export-template"
					label="Template"
				>
					<MultiSelect
						aria-label="Selecionar templates"
						className="w-full"
						emptyStateDescription="Tente outro termo de busca."
						emptyStateTitle="Nenhum template encontrado"
						icon={SearchLg}
						isInvalid={!!templatesError}
						items={templateItems}
						onSelectionChange={handleTemplatesSelectionChange}
						placeholder="Digite ou selecione o nome do template"
						popoverClassName="z-[100000]"
						selectedCountFormatter={(count) =>
							count === 1 ? "1 template" : `${count} templates`
						}
						selectedKeys={selectedKeys}
						showFooter={false}
					>
						{(item) => (
							<MultiSelect.Item
								id={item.id}
								label={item.label}
								selectionIndicator="checkmark"
								textValue={item.label}
							/>
						)}
					</MultiSelect>
				</FormFieldControl>

				<FormFieldControl
					className="xs:grid xs:grid-cols-[85px_minmax(0,1fr)] xs:items-start xs:gap-4"
					error={dateError ?? undefined}
					htmlFor="inspections-export-date"
					label="Data"
				>
					<DateRangePicker
						aria-label="Período de exportação de vistorias"
						buttonClassName="w-full justify-start px-4"
						className="w-full"
						onChange={(range) => {
							setDateRange(range);
							setDateError(null);
						}}
						onPullAllSinceStartChange={(value) => {
							setPullAllSinceStart(value);
							if (value) {
								setDateRange(null);
							}
							setDateError(null);
						}}
						pullAllSinceStart={pullAllSinceStart}
						showPullAllSinceStart
						value={dateRange}
					/>
				</FormFieldControl>
			</ExportRequestModal>

			<ExportSuccessModal
				isOpen={isSuccessOpen}
				message={successMessage}
				onClose={() => setIsSuccessOpen(false)}
			/>
		</>
	);
}
